import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'

interface InnerHeroProps {
  eyebrow?: string
  title: string
  titleItalic?: string
  body?: string
  image?: string
  imageAlt?: string
  breadcrumbs?: Array<{ label: string; href?: string }>
}

export function InnerHero({
  title,
  titleItalic,
  body,
  image,
  imageAlt = '',
  breadcrumbs,
}: InnerHeroProps) {
  const ref = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  })
  const yImage = useTransform(scrollYProgress, [0, 1], ['0%', '20%'])
  const opacityFrame = useTransform(scrollYProgress, [0, 0.5], [1, 0.4])

  return (
    <section
      ref={ref}
      className="relative w-full overflow-hidden bg-cream"
      aria-label={title}
    >
      <div className={`relative overflow-hidden ${image ? 'h-[55svh] min-h-[420px] md:h-[65svh] md:min-h-[520px]' : 'h-auto'}`}>
        {image ? (
          <>
            <motion.div
              style={{ y: yImage }}
              aria-hidden="true"
              className="absolute inset-0 -bottom-[20%]"
            >
              <img
                src={image}
                alt={imageAlt}
                className="absolute inset-0 w-full h-full object-cover animate-ken-burns"
              />
            </motion.div>

            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'linear-gradient(180deg, rgba(20,15,12,0.20) 0%, rgba(20,15,12,0.05) 35%, rgba(20,15,12,0.55) 100%)',
              }}
            />
            <div className="nashi-grain" />
          </>
        ) : (
          <>
            {/* Fallback editorial backdrop when no image is provided */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(207,169,128,0.18)_0%,_rgba(255,250,243,0)_60%)]" />
            <div className="absolute inset-0 bg-cream-soft/60" />
            <div className="nashi-grain" />
          </>
        )}

        {/* Editorial frame */}
        <motion.div
          style={{ opacity: opacityFrame }}
          aria-hidden="true"
          className={`pointer-events-none absolute inset-6 md:inset-10 border ${
            image ? 'border-cream/15' : 'border-bruno/10'
          }`}
        />

        {/* Decorative oversized italic letter */}
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute right-[-3%] top-1/2 -translate-y-1/2 select-none font-display italic ${
            image ? 'text-cream/[0.06]' : 'text-argan/15'
          }`}
          style={{
            fontSize: 'clamp(14rem, 32vw, 32rem)',
            lineHeight: 0.85,
            letterSpacing: '-0.05em',
          }}
        >
          n
        </div>

        {/* Content */}
        <div className={`relative z-10 nashi-container flex flex-col ${image ? 'h-full justify-end pb-14 md:pb-20' : 'py-12 md:py-20'}`}>
          {breadcrumbs && breadcrumbs.length > 0 && (
            <motion.nav
              aria-label="Percorso"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className={`mb-8 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.28em] ${
                image ? 'text-cream/70' : 'text-bruno-muted'
              }`}
            >
              {breadcrumbs.map((b, i) => (
                <span key={`${b.label}-${i}`} className="inline-flex items-center gap-2">
                  {b.href ? (
                    <a
                      href={b.href}
                      className={`hover:underline underline-offset-4 ${
                        image ? 'hover:text-cream' : 'hover:text-bruno'
                      } transition-colors`}
                    >
                      {b.label}
                    </a>
                  ) : (
                    <span className={image ? 'text-cream/95' : 'text-bruno'}>
                      {b.label}
                    </span>
                  )}
                  {i < breadcrumbs.length - 1 && (
                    <span aria-hidden="true" className="opacity-60">
                      /
                    </span>
                  )}
                </span>
              ))}
            </motion.nav>
          )}

          <div className="max-w-3xl">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.05, delay: 0.32, ease: [0.25, 1, 0.5, 1] }}
              className={`mt-5 font-display ${image ? 'text-cream' : 'text-bruno'}`}
              style={{
                fontSize: 'clamp(2.5rem, 6vw, 5.25rem)',
                lineHeight: 0.98,
                letterSpacing: '-0.02em',
              }}
            >
              <span className="block">{title}</span>
              {titleItalic && (
                <span
                  className={`block italic font-light ${
                    image ? 'text-argan-soft' : 'text-argan-deep'
                  }`}
                >
                  {titleItalic}
                </span>
              )}
            </motion.h1>

            {body && (
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.5 }}
                className={`mt-8 max-w-xl text-base md:text-lg leading-relaxed ${
                  image ? 'text-cream/85' : 'text-bruno-soft'
                }`}
              >
                {body}
              </motion.p>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
